import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import Combobox from "@/components/Combobox";
import { adminCreatePost, adminUpdatePost, adminGetPost, adminListCategories } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  category: "",
  read_minutes: 5,
  cover_image: "",
  published_at: "",
  slug: "",
  published: true,
};

const FORMAT_HINT = `Formatting tips:
## Big heading       — for major sections
### Small heading    — for sub-sections
- bullet item        — for unordered lists
1. numbered item     — for ordered lists
**bold text**        — for emphasis
*italic text*        — for italics
(blank line between paragraphs)`;

export default function AdminPostEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    adminListCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    adminGetPost(id)
      .then((p) => setForm({ ...EMPTY, ...p, cover_image: p.cover_image || "" }))
      .catch(() => toast.error("Couldn't load post."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const upd = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.excerpt || !form.content || !form.category) {
      toast.error("Title, excerpt, content, and category are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        category: form.category,
        read_minutes: Number(form.read_minutes) || 5,
        cover_image: form.cover_image || null,
        published_at: form.published_at || null,
        slug: form.slug || null,
        published: !!form.published,
      };
      const saved = isEdit ? await adminUpdatePost(id, payload) : await adminCreatePost(payload);
      toast.success(
        isEdit
          ? `Updated "${saved.title}".`
          : `Created "${saved.title}". ${saved.published ? "Live now." : "Saved as draft."}`
      );
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-ink-muted text-sm" data-testid="editor-loading">Loading post…</div>;
  }

  return (
    <div data-testid="admin-editor-page">
      <Link
        to="/admin"
        data-testid="editor-back"
        className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-forest link-underline mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to posts
      </Link>

      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="overline text-terracotta">{isEdit ? "Edit post" : "New post"}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink mt-2">
            {isEdit ? form.title || "Edit post" : "Write a new post"}
          </h1>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="admin-editor-form"
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        <div className="lg:col-span-2 space-y-5 bg-paper-100 border border-paper-200 p-6 rounded-sm">
          <div>
            <Label htmlFor="title" className="text-ink font-semibold">Title *</Label>
            <Input
              id="title"
              value={form.title}
              onChange={upd("title")}
              data-testid="editor-title"
              className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
              placeholder="A First-Gen Student's Guide to..."
              required
            />
          </div>
          <div>
            <Label htmlFor="excerpt" className="text-ink font-semibold">Excerpt *</Label>
            <Textarea
              id="excerpt"
              value={form.excerpt}
              onChange={upd("excerpt")}
              data-testid="editor-excerpt"
              rows={2}
              className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2 resize-none"
              placeholder="One or two sentence preview that shows on the blog list."
              required
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <Label htmlFor="content" className="text-ink font-semibold">Content *</Label>
              <span className="text-xs text-ink-muted">Markdown supported</span>
            </div>
            <Textarea
              id="content"
              value={form.content}
              onChange={upd("content")}
              data-testid="editor-content"
              rows={20}
              className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2 font-mono text-sm leading-relaxed"
              placeholder="Write the post here. Leave a blank line between paragraphs."
              required
            />
            <pre className="mt-3 text-[11px] text-ink-muted bg-paper-50 border border-paper-200 rounded-sm p-3 whitespace-pre-wrap">
{FORMAT_HINT}
            </pre>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="bg-paper-100 border border-paper-200 p-6 rounded-sm space-y-5">
            <div className="flex items-center justify-between">
              <Label className="text-ink font-semibold flex items-center gap-2">
                {form.published ? <Eye className="w-4 h-4 text-forest" /> : <EyeOff className="w-4 h-4 text-ink-muted" />}
                {form.published ? "Published" : "Draft"}
              </Label>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))}
                data-testid="editor-published"
              />
            </div>
            <p className="text-xs text-ink-muted -mt-2">
              {form.published
                ? "This post will be visible on thestemvet.com/blog."
                : "Only you can see this draft. Toggle on when ready to publish."}
            </p>

            <div>
              <Label htmlFor="category" className="text-ink font-semibold">Category *</Label>
              <Combobox
                id="category"
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                options={categories}
                placeholder="Admissions, MMI, Study Strategy..."
                testid="editor-category"
              />
              <p className="text-xs text-ink-muted mt-1.5">
                Pick one of your existing categories or type a new one.
              </p>
            </div>
            <div>
              <Label htmlFor="read_minutes" className="text-ink font-semibold">Read time (min)</Label>
              <Input
                id="read_minutes"
                type="number"
                min={1}
                max={60}
                value={form.read_minutes}
                onChange={upd("read_minutes")}
                data-testid="editor-read-minutes"
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
              />
            </div>
            <div>
              <Label htmlFor="published_at" className="text-ink font-semibold">Published date</Label>
              <Input
                id="published_at"
                type="date"
                value={form.published_at || ""}
                onChange={upd("published_at")}
                data-testid="editor-published-at"
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
              />
              <p className="text-xs text-ink-muted mt-1">Leave blank for today.</p>
            </div>
            <div>
              <Label htmlFor="cover_image" className="text-ink font-semibold">Cover image URL</Label>
              <Input
                id="cover_image"
                value={form.cover_image}
                onChange={upd("cover_image")}
                data-testid="editor-cover-image"
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                placeholder="https://..."
              />
              {form.cover_image && (
                <img
                  src={form.cover_image}
                  alt=""
                  className="mt-3 w-full aspect-[16/10] object-cover rounded-sm border border-paper-200"
                />
              )}
              <p className="text-xs text-ink-muted mt-2">
                Drop a photo into chat with Claude to get a hosted URL, then paste it here.
              </p>
            </div>
            <div>
              <Label htmlFor="slug" className="text-ink font-semibold">URL slug</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={upd("slug")}
                data-testid="editor-slug"
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                placeholder="auto-generated from title"
              />
              <p className="text-xs text-ink-muted mt-1">
                Leave blank to auto-generate. Posts live at /blog/{form.slug || "<auto>"}.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="editor-save"
            className="w-full inline-flex items-center justify-center gap-2 bg-forest text-paper-50 px-5 py-3.5 text-sm font-semibold rounded-sm hover:bg-forest-700 transition-colors disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create post"}
          </button>
        </aside>
      </form>
    </div>
  );
}
